import { useEffect } from 'react';
import { SITE_URL } from '@/lib/seo';
import { useLocale, localePath, localeAlternates, type Locale } from '@/lib/locale';

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: string;
  noindex?: boolean;
  locale?: Locale;
}

export function Seo({ title, description, path, ogImage = "/og-image.jpg", ogImageWidth = 1200, ogImageHeight = 630, ogType = "website", noindex = false, locale: explicitLocale }: SeoProps) {
  const context = useLocale();
  const locale = explicitLocale ?? context.locale;
  useEffect(() => {
    // Keep the document language aligned during client-side navigation too.
    document.documentElement.lang = locale;
    // Preserve the complete, language-specific metadata produced at build time
    // on a direct page load. On client-side navigation, use the page's rendered
    // heading and introductory copy rather than leaving metadata in the source
    // language.
    const url = `${SITE_URL}${localePath(path, locale)}`;
    const currentCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const isPreRenderedRoute = currentCanonical?.href === url;
    const pageHeading = document.querySelector<HTMLElement>("#root h1")?.innerText.replace(/\s+/g, " ").trim();
    const headingElement = document.querySelector<HTMLElement>("#root h1");
    const pageIntro = headingElement
      ? Array.from(document.querySelectorAll<HTMLElement>("#root p"))
          .find((paragraph) =>
            (headingElement.compareDocumentPosition(paragraph) & Node.DOCUMENT_POSITION_FOLLOWING) &&
            paragraph.innerText.trim().length >= 55
          )
          ?.innerText.replace(/\s+/g, " ").trim()
      : undefined;
    const pageTitle = isPreRenderedRoute || !pageHeading ? title : `${pageHeading} | OVVO Camping`;
    const pageDescription = isPreRenderedRoute || !pageIntro ? description : pageIntro;
    document.title = pageTitle;

    // 2. Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);

    // 3. Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
    document.querySelectorAll('link[rel="alternate"][data-locale]').forEach((node) => node.remove());
    Object.entries(localeAlternates(path)).forEach(([alternateLocale, alternatePath]) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alternateLocale;
      link.href = `${SITE_URL}${alternatePath}`;
      link.dataset.locale = alternateLocale;
      document.head.appendChild(link);
    });
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = `${SITE_URL}${localePath(path, 'it')}`;
    xDefault.dataset.locale = 'x-default';
    document.head.appendChild(xDefault);

    // Helper to set meta tags
    const setMetaTag = (attribute: string, value: string, content: string) => {
      let tag = document.querySelector(`meta[${attribute}="${value}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, value);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // 4. Open Graph Tags
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDescription);
    setMetaTag('property', 'og:url', url);
    setMetaTag('property', 'og:type', ogType);
    const absoluteOgImage = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;
    setMetaTag('property', 'og:image', absoluteOgImage);
    setMetaTag('property', 'og:image:width', String(ogImageWidth));
    setMetaTag('property', 'og:image:height', String(ogImageHeight));
    const ogImageAlt = locale === "en"
      ? "OVVO Camping — cloud management software for outdoor hospitality"
      : locale === "de"
        ? "OVVO Camping — Cloud-Verwaltungssoftware für Campingplätze"
        : locale === "hr"
          ? "OVVO Camping — upravljački softver u oblaku za kampove"
          : "OVVO Camping — gestionale cloud per strutture open-air";
    setMetaTag('property', 'og:image:alt', ogImageAlt);

    // 4b. Robots (noindex per pagine 404 / non canoniche)
    if (noindex) {
      setMetaTag('name', 'robots', 'noindex');
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }

    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDescription);
    setMetaTag('name', 'twitter:image', absoluteOgImage);

  }, [title, description, path, ogImage, ogImageWidth, ogImageHeight, ogType, noindex, locale]);

  return null;
}
